'use client';

import * as React from 'react';
import { useSearchParams } from 'next/navigation';
import { AnyNsRecord } from 'dns';

export interface AuthDrawerProps extends React.HTMLAttributes<HTMLDivElement> {
  onClose?: () => void;
}

export function AuthDrawer({ onClose, ...props }: AuthDrawerProps) {
  const searchParams = useSearchParams();
  const [iframeUrl, setIframeUrl] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  // Generate random 6-digit connection ID
  const generateConnectionId = React.useCallback(() => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  }, []);

  // Fetch encrypted payload and construct iframe URL
  React.useEffect(() => {
    const fetchEncryptedPayload = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Get query parameters
        const userId = searchParams?.get('u');
        const cudbServiceId = searchParams?.get('o');
        const pageId = searchParams?.get('pageId');

        if (!userId || !cudbServiceId || !pageId) {
          throw new Error('Missing required query parameters: u, o, or pageId');
        }

        // Generate connection ID
        const connectionId = generateConnectionId();

        // Call encrypt API
        const encryptApiUrl = process.env.NEXT_PUBLIC_ENCRYPT_API_URL;
        if (!encryptApiUrl) {
          throw new Error('NEXT_PUBLIC_ENCRYPT_API_URL is not configured');
        }

        const response = await fetch(`${encryptApiUrl}/encryptPayloadTopic`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            payload: JSON.stringify({
              userId,
              connectionId,
              cudbServiceId,
            })
          })
        });

        console.log(response);

        // Construct iframe URL
        const iframeBaseUrl = process.env.NEXT_PUBLIC_AUTH_IFRAME_BASE_URL;
        if (!iframeBaseUrl) {
          throw new Error('NEXT_PUBLIC_AUTH_IFRAME_BASE_URL is not configured');
        }

        // Parse JSON response body to get encryptedPayload
        const data = await response.json();
        if (!data?.data?.encryptedPayload) {
          throw new Error('Failed to retrieve encryptedPayload from response');
        }
        const url = `${iframeBaseUrl}/auth/login?token=${encodeURIComponent(data.data.encryptedPayload)}&redirect=page/${encodeURIComponent(pageId)}`;
        setIframeUrl(url);
      } catch (err) {
        console.error('Error fetching encrypted payload:', err);
        setError(err instanceof Error ? err.message : 'Failed to load authentication');
      } finally {
        setIsLoading(false);
      }
    };

    fetchEncryptedPayload();
  }, [searchParams, generateConnectionId]);

  return (
    <div className="lk-auth-drawer" {...props}>
      <div className="lk-auth-drawer-content">
        <div className="lk-auth-drawer-header">
          <h2>Chat</h2>
          <span
            className="lk-auth-close-button"
            role="button"
            tabIndex={0}
            onClick={() => {
              if (onClose) {
                onClose();
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                if (onClose) {
                  onClose();
                }
              }
            }}
            aria-label="Close"
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: 'none',
              border: 'none',
              fontSize: 22,
              color: '#000',
              outline: 'none',
            }}
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 22 22"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              focusable="false"
            >
              <line x1="5" y1="5" x2="17" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <line x1="17" y1="5" x2="5" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </span>
        </div>
        <div className="lk-auth-drawer-body">
          {isLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <div className="spinner" />
            </div>
          )}
          {error && (
            <div style={{ padding: '2rem', color: '#000', textAlign: 'center' }}>
              <p style={{ margin: 0 }}>Error: {error}</p>
            </div>
          )}
          {iframeUrl && !isLoading && !error && (
            <iframe
              src={iframeUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              title="Authentication"
            />
          )}
        </div>
      </div>
      <div
        className="lk-auth-drawer-overlay"
        onClick={() => {
          if (onClose) {
            onClose();
          }
        }}
      />
    </div>
  );
}

